/**
 * Tools the AI can call, in the OpenAI "function calling" format that LM Studio speaks.
 *
 * Two kinds:
 *   Read tools     answer questions from the ledger (balances, items, receipts, history).
 *   Propose tools  never change anything. They build the exact commands a change would need,
 *                  check them with decide(), and return a plain-language summary. The app shows
 *                  that as a card with Yes / No, and only a person tapping Yes applies it.
 *
 * Everything here is pure: it takes the current state and returns data, so the same tools can
 * later back an MCP server or a Discord bot without changes.
 */
import { apply } from "../apply.ts";
import type { Command, CommandEnvelope } from "../commands.ts";
import { decide } from "../decide.ts";
import { computeLedger, type Ledger } from "../engine/ledger.ts";
import { splitItem } from "../engine/split.ts";
import { buildHistory } from "../history.ts";
import { formatBp, formatCents, formatMicro } from "../money.ts";
import type { StoredEvent } from "../events.ts";
import { receiptKey, type GroupState, type Item, type ItemFields, type Person } from "../types.ts";

export interface ToolContext {
  state: GroupState;
  ledger: Ledger;
  events: readonly StoredEvent[];
  today: string;
  /** The local time now as "HH:MM" (used as a new payment's time). Passed in to keep tools pure. */
  time: string;
  /** Makes ids for new items and payments. */
  newId: () => string;
}

export type ToolOutcome =
  | { kind: "result"; data: unknown; label: string }
  | { kind: "proposal"; summary: string; commands: Command[]; label: string }
  | { kind: "error"; message: string; label: string };

type Json = Record<string, unknown>;

/** OpenAI-style tool definitions sent to the model. */
export const TOOL_DEFS = [
  tool(
    "get_overview",
    "Totals for the group: who owes how much, what's unassigned, trip count.",
    {},
  ),
  tool(
    "get_person",
    "One person's total, payments, balance and unpaid items.",
    {
      name: { type: "string", description: "Person's name, e.g. Emily" },
    },
    ["name"],
  ),
  tool("list_items", "Find items. All filters are optional and combine.", {
    person: { type: "string", description: "Only items this person is ticked on" },
    store: { type: "string" },
    from: { type: "string", description: "Earliest date, YYYY-MM-DD" },
    to: { type: "string", description: "Latest date, YYYY-MM-DD" },
    unpaid_only: { type: "boolean", description: "Only items someone still owes for" },
    query: { type: "string", description: "Words in the description or the item number" },
    limit: { type: "integer", description: "Maximum rows (default 25)" },
  }),
  tool("get_trips", "Each trip (date + store) with its total and receipt check.", {}),
  tool(
    "lookup_catalog",
    "Look up an item number or name bought before, with its last price.",
    {
      query: { type: "string" },
    },
    ["query"],
  ),
  tool("recent_changes", "The most recent changes in the history, newest first.", {
    limit: { type: "integer", description: "How many (default 10)" },
  }),
  tool(
    "propose_add_item",
    "Propose adding an item. The user must confirm before anything changes.",
    {
      name: { type: "string" },
      price: { type: "number", description: "Price in dollars for one unit, before tax" },
      store: { type: "string", description: "Defaults to the store used last" },
      date: { type: "string", description: "YYYY-MM-DD, defaults to today" },
      quantity: { type: "integer", description: "Defaults to 1" },
      item_no: { type: "string", description: "Costco item number, if known" },
      tax_percent: { type: "number", description: "Leave out to use the store's preset" },
      people: { type: "array", items: { type: "string" }, description: "Names of who shares it" },
      split: { type: "string", description: "Uneven split like 'Emily 2, Chloe 1' or 'Emily $10'" },
    },
    ["name", "price", "people"],
  ),
  tool(
    "propose_payment",
    "Propose logging a payment. The user must confirm.",
    {
      person: { type: "string" },
      amount: { type: "number", description: "Dollars, positive" },
      direction: { type: "string", enum: ["they_paid_me", "i_paid_them"] },
      note: { type: "string", description: "e.g. Zelle, Venmo, cash" },
      date: { type: "string", description: "YYYY-MM-DD, defaults to today" },
    },
    ["person", "amount"],
  ),
  tool(
    "propose_tick",
    "Propose ticking or unticking a person on an existing item. The user must confirm.",
    {
      item: { type: "string", description: "Item description or item number" },
      person: { type: "string" },
      ticked: { type: "boolean", description: "true to add them, false to remove" },
    },
    ["item", "person", "ticked"],
  ),
  tool(
    "propose_assign",
    "Propose who got what on one receipt, e.g. 'Nick got the water and eggs, Carrie and I split the chicken'. Leave store and date out for the newest receipt. Each item can be a name, part of a name, or an item number. The user must confirm.",
    {
      store: { type: "string" },
      date: { type: "string", description: "YYYY-MM-DD" },
      assignments: {
        type: "array",
        items: {
          type: "object",
          properties: {
            item: { type: "string" },
            people: { type: "array", items: { type: "string" } },
          },
          required: ["item", "people"],
        },
      },
      only: {
        type: "boolean",
        description: "true: those people only (untick anyone else on those items)",
      },
    },
    ["assignments"],
  ),
  tool(
    "propose_receipt_total",
    "Propose setting the printed total for a trip's receipt. The user must confirm.",
    {
      store: { type: "string" },
      date: { type: "string", description: "YYYY-MM-DD" },
      total: { type: "number", description: "Dollars" },
    },
    ["store", "date", "total"],
  ),
] as const;

function tool(name: string, description: string, properties: Json, required: string[] = []) {
  return {
    type: "function" as const,
    function: { name, description, parameters: { type: "object", properties, required } },
  };
}

/** A compact picture of the ledger for the system prompt, so even models without tool use can answer. */
export function snapshotText(ctx: Pick<ToolContext, "state" | "ledger" | "today">): string {
  const { state, ledger } = ctx;
  const owner = state.settings.ownerPersonId
    ? state.people[state.settings.ownerPersonId]?.name
    : null;
  const lines = [
    `Group: ${state.name || "Shared expenses"}. Today is ${ctx.today}.${owner ? ` The user is ${owner}.` : ""}`,
    `All purchases: ${formatMicro(ledger.totals.allMicro)} across ${ledger.trips.length} trips.`,
    "People (total of their shares / paid / balance; positive balance = they owe the user):",
    ...ledger.people.map(
      (p) =>
        `- ${p.name}${p.status === "you" ? " (the user)" : ""}: ${formatMicro(p.totalMicro)} / ${formatCents(p.paidCents)} / ${formatMicro(p.balanceMicro)}`,
    ),
    "Recent trips:",
    ...ledger.trips
      .slice(0, 8)
      .map((t) => `- ${t.date} ${t.store}: ${t.itemCount} items, ${formatMicro(t.totalMicro)}`),
  ];
  return lines.join("\n");
}

export function runTool(name: string, rawArgs: unknown, ctx: ToolContext): ToolOutcome {
  const args = (rawArgs && typeof rawArgs === "object" ? rawArgs : {}) as Json;
  try {
    switch (name) {
      case "get_overview":
        return overview(ctx);
      case "get_person":
        return getPerson(ctx, str(args.name));
      case "list_items":
        return listItems(ctx, args);
      case "get_trips":
        return {
          kind: "result",
          label: "Checked the receipts",
          data: ctx.ledger.trips.map((t) => ({
            date: t.date,
            store: t.store,
            items: t.itemCount,
            total: formatMicro(t.totalMicro),
            receipt: t.receiptCents === null ? null : formatCents(t.receiptCents),
            difference: t.diffMicro === null ? null : formatMicro(t.diffMicro),
          })),
        };
      case "lookup_catalog":
        return lookupCatalog(ctx, str(args.query));
      case "recent_changes": {
        const limit = clampInt(args.limit, 10, 1, 50);
        const h = buildHistory(ctx.state.groupId, ctx.events).slice(-limit).reverse();
        return {
          kind: "result",
          label: "Read the recent history",
          data: h.map((e) => ({ when: e.event.at, what: e.text, changes: e.changes })),
        };
      }
      case "propose_add_item":
        return proposeAddItem(ctx, args);
      case "propose_payment":
        return proposePayment(ctx, args);
      case "propose_tick":
        return proposeTick(ctx, args);
      case "propose_assign":
        return proposeAssign(ctx, args);
      case "propose_receipt_total":
        return proposeReceipt(ctx, args);
      default:
        return {
          kind: "error",
          label: `Unknown tool ${name}`,
          message: `There's no tool called ${name}.`,
        };
    }
  } catch (e) {
    return {
      kind: "error",
      label: `${name} failed`,
      message: e instanceof Error ? e.message : String(e),
    };
  }
}

// ------------------------------------------------------------------ read tools

function overview(ctx: ToolContext): ToolOutcome {
  const { ledger } = ctx;
  return {
    kind: "result",
    label: "Looked at the overall balances",
    data: {
      total_spent: formatMicro(ledger.totals.allMicro),
      still_owed_to_user: formatMicro(ledger.totals.outstandingMicro),
      unassigned: formatMicro(ledger.totals.unassignedMicro),
      trips: ledger.trips.length,
      people: ledger.people.map((p) => ({
        name: p.name,
        status: p.status,
        total: formatMicro(p.totalMicro),
        paid: formatCents(p.paidCents),
        balance: formatMicro(p.balanceMicro),
        days_overdue: p.status === "owes" ? p.daysOverdue : 0,
      })),
    },
  };
}

function getPerson(ctx: ToolContext, name: string): ToolOutcome {
  const person = findPerson(ctx.state, name);
  if (!person.ok) return { kind: "error", label: `Looked for ${name}`, message: person.message };
  const p = ctx.ledger.byPersonId[person.value.id]!;
  const payments = ctx.state.paymentOrder
    .map((id) => ctx.state.payments[id]!)
    .filter((x) => x && x.personId === person.value.id)
    .map((x) => ({ when: x.at, amount: formatCents(x.cents), note: x.note }));
  return {
    kind: "result",
    label: `Looked up ${p.name}`,
    data: {
      name: p.name,
      status: p.status,
      items_total: formatMicro(p.totalMicro),
      paid: formatCents(p.paidCents),
      paid_stores_for_group: formatMicro(p.frontedMicro),
      balance: formatMicro(p.balanceMicro),
      oldest_unpaid: p.oldestUnpaid,
      days_overdue: p.daysOverdue,
      unpaid_items: p.unpaid.map((u) => ({
        name: u.name,
        date: u.date,
        still_owed: formatMicro(u.remainingMicro),
        partly_paid: u.partial,
      })),
      payments,
    },
  };
}

function listItems(ctx: ToolContext, a: Json): ToolOutcome {
  let personId: string | null = null;
  if (a.person) {
    const p = findPerson(ctx.state, str(a.person));
    if (!p.ok) return { kind: "error", label: "Searched items", message: p.message };
    personId = p.value.id;
  }
  const q = str(a.query).toLowerCase();
  const store = str(a.store).toLowerCase();
  const limit = clampInt(a.limit, 25, 1, 100);
  const rows = ctx.ledger.items.filter((c) => {
    if (personId && c.shares[personId] === undefined) return false;
    if (store && c.item.store.toLowerCase() !== store) return false;
    if (a.from && c.item.date < str(a.from)) return false;
    if (a.to && c.item.date > str(a.to)) return false;
    if (a.unpaid_only) {
      const ids = personId ? [personId] : Object.keys(c.status);
      if (
        !ids.some(
          (id) =>
            c.status[id] && c.status[id] !== "paid" && id !== ctx.state.settings.ownerPersonId,
        )
      ) {
        return false;
      }
    }
    if (q && !`${c.item.name} ${c.item.itemNo}`.toLowerCase().includes(q)) return false;
    return true;
  });
  return {
    kind: "result",
    label: `Searched items${a.person ? ` for ${str(a.person)}` : ""}`,
    data: {
      matches: rows.length,
      shown: Math.min(rows.length, limit),
      items: rows.slice(0, limit).map((c) => ({
        date: c.item.date,
        store: c.item.store,
        name: c.item.name,
        item_no: c.item.itemNo || undefined,
        total: formatMicro(c.totalMicro),
        people: c.item.ticked.map((id) => ({
          name: ctx.state.people[id]?.name,
          share: formatMicro(c.shares[id] ?? 0),
          status: c.status[id],
        })),
      })),
    },
  };
}

function lookupCatalog(ctx: ToolContext, query: string): ToolOutcome {
  const q = query.trim().toLowerCase();
  const hits = ctx.ledger.catalog.filter(
    (c) => c.itemNo === q || c.name.toLowerCase().includes(q) || c.itemNo.includes(q),
  );
  return {
    kind: "result",
    label: `Looked up "${query}" in the catalog`,
    data: hits.slice(0, 10).map((c) => ({
      item_no: c.itemNo,
      name: c.name,
      last_price: formatCents(c.lastPriceCents),
      last_bought: c.lastDate,
      store: c.store,
      times_bought: c.timesBought,
    })),
  };
}

// ------------------------------------------------------------------ propose tools

function proposeAddItem(ctx: ToolContext, a: Json): ToolOutcome {
  const people: Person[] = [];
  for (const n of Array.isArray(a.people) ? a.people : []) {
    const p = findPerson(ctx.state, str(n));
    if (!p.ok) return { kind: "error", label: "Drafted an item", message: p.message };
    if (!people.includes(p.value)) people.push(p.value);
  }
  const qty = clampInt(a.quantity, 1, 1, 999);
  const unit = dollars(a.price);
  if (unit === null || unit < 0)
    return {
      kind: "error",
      label: "Drafted an item",
      message: "The price must be a positive number of dollars.",
    };
  const lastItem = ctx.state.items[ctx.state.itemOrder[ctx.state.itemOrder.length - 1] ?? ""];
  const store = str(a.store).trim() || lastItem?.store || "Costco";
  const taxBp =
    a.tax_percent === undefined || a.tax_percent === null
      ? null
      : Math.round(Number(a.tax_percent) * 100);
  if (taxBp !== null && (!Number.isFinite(taxBp) || taxBp < 0 || taxBp > 10_000)) {
    return {
      kind: "error",
      label: "Drafted an item",
      message: "Tax/tip must be between 0 and 100 percent.",
    };
  }
  const date = isoDate(a.date) ?? ctx.today;
  const name = str(a.name).trim();
  const fields: ItemFields = {
    date,
    store: matchStore(ctx.state, store),
    itemNo: str(a.item_no).trim(),
    name: qty > 1 ? `${name} x${qty}` : name,
    category: null,
    priceCents: unit * qty,
    couponCents: 0,
    crvCents: 0,
    taxBp,
    paidWith: null,
    paidBy: null,
    customSplit: str(a.split).trim(),
    note: "",
  };
  const cmd: Command = {
    type: "item.add",
    itemId: ctx.newId(),
    fields,
    ticked: people.map((p) => p.id),
  };
  const check = decide(ctx.state, cmd);
  if (!check.ok) return { kind: "error", label: "Drafted an item", message: check.error.message };
  const draft: Item = { id: "draft", ...fields, ticked: people.map((p) => p.id) };
  const split = splitItem(ctx.state, draft);
  if (split.errors.length)
    return { kind: "error", label: "Drafted an item", message: split.errors.join(" ") };
  const who =
    people.length === 0
      ? "with nobody ticked yet"
      : people.length === 1
        ? `for ${people[0]!.name}`
        : `split ${people.map((p) => `${p.name} ${formatMicro(split.shares[p.id] ?? 0)}`).join(", ")}`;
  const tax =
    (split.taxBp ? ` + ${formatBp(split.taxBp)}` : "") +
    (split.tipBp ? ` + ${formatBp(split.tipBp)} tip` : "");
  const no = fields.itemNo ? ` (#${fields.itemNo})` : "";
  return {
    kind: "proposal",
    label: `Drafted ${fields.name}`,
    commands: [cmd],
    summary: `Add ${fields.name}${no}, ${formatCents(fields.priceCents)}${tax} = ${formatMicro(split.totalMicro)} at ${fields.store} on ${date}, ${who}?`,
  };
}

function proposePayment(ctx: ToolContext, a: Json): ToolOutcome {
  const p = findPerson(ctx.state, str(a.person));
  if (!p.ok) return { kind: "error", label: "Drafted a payment", message: p.message };
  const amount = dollars(a.amount);
  if (amount === null || amount <= 0)
    return {
      kind: "error",
      label: "Drafted a payment",
      message: "The amount must be more than $0.",
    };
  const out = a.direction === "i_paid_them";
  const date = isoDate(a.date) ?? ctx.today;
  const cmd: Command = {
    type: "payment.add",
    paymentId: ctx.newId(),
    at: `${date}T${date === ctx.today ? ctx.time : "12:00"}`,
    personId: p.value.id,
    cents: out ? -amount : amount,
    note: str(a.note).trim().slice(0, 200),
  };
  const check = decide(ctx.state, cmd);
  if (!check.ok) return { kind: "error", label: "Drafted a payment", message: check.error.message };
  const after = computeLedger(applyAll(ctx.state, cmd), ctx.today).byPersonId[p.value.id]!;
  const left =
    after.status === "owes"
      ? ` ${p.value.name} would owe you ${formatMicro(after.balanceMicro)}.`
      : after.status === "owed"
        ? ` You'd owe them ${formatMicro(-after.balanceMicro)}.`
        : " That squares them up.";
  const note = cmd.note ? ` (${cmd.note})` : "";
  return {
    kind: "proposal",
    label: `Drafted a payment for ${p.value.name}`,
    commands: [cmd],
    summary: out
      ? `Log that you paid ${p.value.name} ${formatCents(amount)}${note} on ${date}?${left}`
      : `Log ${formatCents(amount)} from ${p.value.name}${note} on ${date}?${left}`,
  };
}

function proposeTick(ctx: ToolContext, a: Json): ToolOutcome {
  const p = findPerson(ctx.state, str(a.person));
  if (!p.ok) return { kind: "error", label: "Drafted a change", message: p.message };
  const q = str(a.item).trim().toLowerCase();
  const matches = ctx.ledger.items.filter(
    (c) => c.item.itemNo === q || c.item.name.toLowerCase() === q,
  );
  // Fall back to whole-word matching, so "tea" finds "Green Tea" but not "Steak".
  const word = new RegExp(`\\b${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "i");
  const loose = matches.length ? matches : ctx.ledger.items.filter((c) => word.test(c.item.name));
  if (loose.length === 0)
    return { kind: "error", label: "Drafted a change", message: `No item matches "${a.item}".` };
  if (loose.length > 1) {
    return {
      kind: "error",
      label: "Drafted a change",
      message: `"${a.item}" matches ${loose.length} items: ${loose
        .slice(0, 6)
        .map((c) => `${c.item.name} (${c.item.store} ${c.item.date})`)
        .join("; ")}. Ask which one.`,
    };
  }
  const item = loose[0]!.item;
  const ticked = a.ticked !== false;
  const cmd: Command = { type: "share.set", itemId: item.id, personId: p.value.id, ticked };
  const check = decide(ctx.state, cmd);
  if (!check.ok) return { kind: "error", label: "Drafted a change", message: check.error.message };
  if (check.value.length === 0) {
    return {
      kind: "error",
      label: "Drafted a change",
      message: `${p.value.name} is already ${ticked ? "" : "not "}ticked on ${item.name}.`,
    };
  }
  return {
    kind: "proposal",
    label: `Drafted a change to ${item.name}`,
    commands: [cmd],
    summary: `${ticked ? "Add" : "Remove"} ${p.value.name} ${ticked ? "to" : "from"} ${item.name} (${item.store}, ${item.date})?`,
  };
}

/** Who got what on one receipt: share.set for each person on each item named. */
function proposeAssign(ctx: ToolContext, a: Json): ToolOutcome {
  const label = "Drafted who got what";
  const store = str(a.store).trim().toLowerCase();
  const date = isoDate(a.date);
  // The receipt: the one named, else the newest.
  const pool = ctx.ledger.items.filter(
    (c) =>
      (!store || c.item.store.toLowerCase().includes(store)) && (!date || c.item.date === date),
  );
  if (!pool.length)
    return { kind: "error", label, message: "No receipt matches that store and date." };
  const newest = pool.reduce((m, c) => (c.item.date > m.item.date ? c : m));
  const key = (c: (typeof pool)[number]) => receiptKey(c.item);
  const lines = pool.filter((c) => key(c) === key(newest));
  const list = Array.isArray(a.assignments) ? (a.assignments as Json[]) : [];
  if (!list.length) return { kind: "error", label, message: "Say which items and who got them." };
  const commands: Command[] = [];
  const said: string[] = [];
  const ticks = new Map<string, Set<string>>();
  for (const row of list) {
    const q = str(row.item).trim().toLowerCase();
    const word = new RegExp(`\\b${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "i");
    const hits = lines.filter((c) => c.item.itemNo === q || c.item.name.toLowerCase() === q);
    const found = hits.length ? hits : lines.filter((c) => word.test(c.item.name));
    if (found.length !== 1)
      return {
        kind: "error",
        label,
        message: found.length
          ? `"${row.item}" matches ${found.map((c) => c.item.name).join(", ")} on that receipt. Ask which.`
          : `Nothing called "${row.item}" on the ${newest.item.store} receipt from ${newest.item.date}. Its lines: ${lines.map((c) => c.item.name).join(", ")}.`,
      };
    const item = found[0]!.item;
    const people: { id: string; name: string }[] = [];
    for (const name of Array.isArray(row.people) ? (row.people as unknown[]) : []) {
      const n = str(name).trim().toLowerCase();
      if (n === "me" || n === "i" || n === "myself") {
        const owner = ctx.state.settings.ownerPersonId;
        const o = owner ? ctx.state.people[owner] : undefined;
        if (!o)
          return {
            kind: "error",
            label,
            message: "You aren't set up as a person in this group yet.",
          };
        people.push({ id: o.id, name: o.name });
        continue;
      }
      const p = findPerson(ctx.state, str(name));
      if (!p.ok) return { kind: "error", label, message: p.message };
      people.push({ id: p.value.id, name: p.value.name });
    }
    if (!people.length) return { kind: "error", label, message: `Who got ${item.name}?` };
    const want = new Set(people.map((p) => p.id));
    const steps: Extract<Command, { type: "share.set" }>[] = [
      ...people.map((p) => ({
        type: "share.set" as const,
        itemId: item.id,
        personId: p.id,
        ticked: true,
      })),
      ...(a.only === true
        ? item.ticked
            .filter((id) => !want.has(id))
            .map((id) => ({
              type: "share.set" as const,
              itemId: item.id,
              personId: id,
              ticked: false,
            }))
        : []),
    ];
    // Track ticks locally (never touch ctx.state: it's the app's live state).
    const now = ticks.get(item.id) ?? new Set(item.ticked);
    ticks.set(item.id, now);
    for (const cmd of steps) {
      if (cmd.ticked === now.has(cmd.personId)) continue; // already that way
      const check = decide(ctx.state, cmd);
      if (!check.ok) return { kind: "error", label, message: check.error.message };
      commands.push(cmd);
      if (cmd.ticked) now.add(cmd.personId);
      else now.delete(cmd.personId);
    }
    said.push(`${item.name}: ${people.map((p) => p.name).join(" and ")}`);
  }
  if (!commands.length)
    return { kind: "error", label, message: "Everyone's already marked that way." };
  return {
    kind: "proposal",
    label,
    commands,
    summary: `On the ${newest.item.store} receipt from ${newest.item.date}: ${said.join("; ")}${a.only === true ? " (only them)" : ""}?`,
  };
}

function proposeReceipt(ctx: ToolContext, a: Json): ToolOutcome {
  const date = isoDate(a.date);
  const total = dollars(a.total);
  if (!date || total === null || total < 0) {
    return {
      kind: "error",
      label: "Drafted a receipt total",
      message: "I need a date (YYYY-MM-DD) and a total in dollars.",
    };
  }
  const store = matchStore(ctx.state, str(a.store));
  const cmd: Command = { type: "receipt.set", date, store, cents: total };
  const check = decide(ctx.state, cmd);
  if (!check.ok)
    return { kind: "error", label: "Drafted a receipt total", message: check.error.message };
  return {
    kind: "proposal",
    label: `Drafted a receipt total for ${store}`,
    commands: [cmd],
    summary: `Set the ${store} receipt for ${date} to ${formatCents(total)}?`,
  };
}

// ------------------------------------------------------------------ helpers

/** Applies proposal commands to a copy of the state, for previews. */
function applyAll(state: GroupState, ...cmds: Command[]): GroupState {
  let s = structuredClone(state);
  for (const c of cmds) {
    const r = decide(s, c);
    if (r.ok) for (const e of r.value) s = apply(s, e);
  }
  return s;
}

/** Turns a proposal into envelopes the app can enqueue once the user says yes. */
export function proposalEnvelopes(commands: Command[], newId: () => string): CommandEnvelope[] {
  return commands.map((cmd) => ({ cmdId: newId(), cmd }));
}

function findPerson(
  state: GroupState,
  name: string,
): { ok: true; value: Person } | { ok: false; message: string } {
  const n = name.trim().toLowerCase();
  const people = state.personOrder.map((id) => state.people[id]!).filter(Boolean);
  const exact = people.find((p) => p.name.toLowerCase() === n);
  if (exact) return { ok: true, value: exact };
  const partial = people.filter((p) => p.name.toLowerCase().startsWith(n) && n.length > 0);
  if (partial.length === 1) return { ok: true, value: partial[0]! };
  const owner = state.settings.ownerPersonId
    ? state.people[state.settings.ownerPersonId]
    : undefined;
  if (owner && ["me", "i", "myself", "you"].includes(n)) return { ok: true, value: owner };
  return {
    ok: false,
    message: `Nobody here is called "${name}". People: ${people.map((p) => p.name).join(", ")}.`,
  };
}

function matchStore(state: GroupState, store: string): string {
  const s = store.trim();
  const known = [...Object.keys(state.stores), ...Object.values(state.items).map((i) => i.store)];
  return known.find((k) => k.toLowerCase() === s.toLowerCase()) ?? s;
}

function str(v: unknown): string {
  return v === undefined || v === null ? "" : String(v);
}

function dollars(v: unknown): number | null {
  const n = typeof v === "number" ? v : Number(String(v ?? "").replace(/[$,\s]/g, ""));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

function clampInt(v: unknown, def: number, min: number, max: number): number {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && v !== undefined && v !== null
    ? Math.min(max, Math.max(min, n))
    : def;
}

function isoDate(v: unknown): string | null {
  const s = str(v);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}
